import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { FileText, Printer, Loader2, ChevronLeft, ChevronRight, Sun, Moon, AlertTriangle, CalendarDays, Coffee, MapPin, Building, Pencil, Trash2, X, Building2, Home } from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, getWeek } from 'date-fns';
import { it } from 'date-fns/locale';
import { Checkbox } from '../components/ui/checkbox';
import { useNavigate } from 'react-router-dom';
import { getPrintToolbarCSS, getPrintToolbarHTML } from '../utils/printHelper';

const MEALS = [
  { code: 'colazione', name: 'Colazione', icon: Coffee, color: 'amber', shortLabel: 'COL' },
  { code: 'pranzo', name: 'Pranzo', icon: Sun, color: 'orange', shortLabel: 'P' },
  { code: 'cena', name: 'Cena', icon: Moon, color: 'indigo', shortLabel: 'C' }
];

export const PatientSheetsPage = () => {
  const { api, activeStructure, canEditPatientSheets } = useAuth();
  const navigate = useNavigate();
  const [sheetsData, setSheetsData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [allergies, setAllergies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [mealType, setMealType] = useState('pranzo');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [floorFilter, setFloorFilter] = useState('all');
  const [houseFilter, setHouseFilter] = useState('all'); // Filtro per casa
  const printRef = useRef(null);
  
  // Edit/Delete state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [editOrders, setEditOrders] = useState({});
  const [menuItems, setMenuItems] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Print options state
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printSheetsPerPage, setPrintSheetsPerPage] = useState('4'); // '4' or '6'
  const [printOrientation, setPrintOrientation] = useState('portrait'); // 'portrait' or 'landscape'
  const [printGroupByHouse, setPrintGroupByHouse] = useState(true); // Raggruppa per casa

  const structureName = activeStructure?.name || '';
  const isMultiHouse = activeStructure?.is_multi_house || false;
  const houses = activeStructure?.houses || [];

  const dateString = format(selectedDate, 'yyyy-MM-dd');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sheetsRes, categoriesRes, conditionsRes, allergiesRes] = await Promise.all([
        api.get('/kitchen/patient-sheets', { params: { date: dateString, meal_type: mealType } }),
        api.get('/categories'),
        api.get('/conditions'),
        api.get('/allergies')
      ]);
      setSheetsData(sheetsRes.data);
      setCategories(categoriesRes.data);
      setConditions(conditionsRes.data);
      setAllergies(allergiesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  }, [api, dateString, mealType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const navigateDate = (direction) => {
    setSelectedDate(prev => addDays(prev, direction));
  };

  const handleDateSelect = (date) => {
    if (date) {
      setSelectedDate(date);
      setCalendarOpen(false);
    }
  };

  // Handle Delete Orders for a patient
  const handleDeleteOrders = async (patient) => {
    if (!window.confirm(`Eliminare tutti gli ordini di ${patient.patient_name} per ${mealType}?`)) {
      return;
    }
    
    try {
      await api.delete('/orders/patient-meal', {
        params: {
          patient_id: patient.patient_id,
          date: dateString,
          meal_type: mealType
        }
      });
      toast.success('Ordini eliminati');
      fetchData(); // Refresh
    } catch (error) {
      console.error('Failed to delete orders:', error);
      toast.error('Errore nell\'eliminazione');
    }
  };

  // Handle Edit Orders - Open dialog
  const handleEditOrders = async (patient) => {
    setEditingPatient(patient);
    
    // Load menu for this day/meal
    try {
      const weekNumber = getWeek(selectedDate, { weekStartsOn: 1 });
      const dayNames = ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'];
      const dayCode = dayNames[selectedDate.getDay()];
      
      const menuRes = await api.get('/menu/by-day', {
        params: { week_number: weekNumber, day: dayCode, meal_type: mealType }
      });
      
      const menuByCategory = {};
      menuRes.data.forEach(item => {
        menuByCategory[item.category_code] = {
          dish_name: item.dish_name,
          allergens: item.allergens || [],
          side_dish: item.side_dish,
          side_dish_allergens: item.side_dish_allergens || []
        };
      });
      setMenuItems(menuByCategory);
      
      // Set current orders as selected
      const currentOrders = {};
      patient.orders.forEach(order => {
        currentOrders[order.category] = {
          selected: true,
          variant: order.variant || 'normale',
          note: order.note || ''
        };
      });
      setEditOrders(currentOrders);
      
      setEditDialogOpen(true);
    } catch (error) {
      console.error('Failed to load menu:', error);
      toast.error('Errore nel caricamento menu');
    }
  };

  // Save edited orders
  const handleSaveEdit = async () => {
    if (!editingPatient) return;
    
    setSavingEdit(true);
    try {
      const weekNumber = getWeek(selectedDate, { weekStartsOn: 1 });
      const dayNames = ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'];
      const dayCode = dayNames[selectedDate.getDay()];
      
      // First delete all existing orders
      await api.delete('/orders/patient-meal', {
        params: {
          patient_id: editingPatient.patient_id,
          date: dateString,
          meal_type: mealType
        }
      });
      
      // Then save selected orders
      const items = [];
      Object.entries(editOrders).forEach(([categoryCode, order]) => {
        if (order.selected) {
          const menuData = menuItems[categoryCode] || {};
          items.push({
            category_code: categoryCode,
            dish_name: menuData.dish_name || categoryCode,
            allergens: menuData.allergens || [],
            condition_variant: order.variant || 'normale',
            note: order.note || ''
          });
        }
      });
      
      if (items.length > 0) {
        await api.post('/orders/bulk', {
          patient_id: editingPatient.patient_id,
          patient_name: editingPatient.patient_name,
          patient_room: editingPatient.patient_room,
          patient_conditions: editingPatient.patient_conditions || [],
          week_number: weekNumber,
          day: dayCode,
          date: dateString,
          meal_type: mealType,
          items
        });
      }
      
      toast.success('Ordini aggiornati');
      setEditDialogOpen(false);
      setEditingPatient(null);
      fetchData(); // Refresh
    } catch (error) {
      console.error('Failed to save edit:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setSavingEdit(false);
    }
  };

  // Toggle order in edit mode
  const toggleEditOrder = (categoryCode) => {
    setEditOrders(prev => {
      const current = prev[categoryCode];
      if (current?.selected) {
        const { [categoryCode]: removed, ...rest } = prev;
        return rest;
      } else {
        return {
          ...prev,
          [categoryCode]: { selected: true, variant: 'normale', note: '' }
        };
      }
    });
  };

  // Get available floors from patients
  const availableFloors = [...new Set(
    (sheetsData?.patients || [])
      .map(p => p.patient_room?.charAt(0))
      .filter(Boolean)
  )].sort();

  // Filter and sort patients
  const getFilteredPatients = () => {
    let patients = (sheetsData?.patients || []).filter(p => p.meal_type === mealType);
    
    // Filter by house (for multi-house structures)
    if (houseFilter !== 'all') {
      patients = patients.filter(p => p.house_id === houseFilter);
    }
    
    // Filter by floor
    if (floorFilter !== 'all') {
      patients = patients.filter(p => p.patient_room?.charAt(0) === floorFilter);
    }
    
    // Sort by house prefix (if multi-house), then by room number (ascending)
    patients.sort((a, b) => {
      if (isMultiHouse) {
        const houseA = a.house_prefix || '';
        const houseB = b.house_prefix || '';
        if (houseA !== houseB) return houseA.localeCompare(houseB);
      }
      const roomA = parseInt(a.patient_room) || 0;
      const roomB = parseInt(b.patient_room) || 0;
      return roomA - roomB;
    });
    
    return patients;
  };

  const handlePrint = () => {
    // Get patients filtered and sorted
    const patients = getFilteredPatients();
    
    if (patients.length === 0) {
      toast.error('Nessun foglio da stampare');
      return;
    }
    
    const mealConfig = MEALS.find(m => m.code === mealType);
    const mealLabel = mealConfig?.name.toUpperCase() || 'PRANZO';
    const dateLabel = format(selectedDate, 'd MMMM yyyy', { locale: it });
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Popup bloccato - abilita i popup per stampare');
      return;
    }
    
    // Dynamic grid based on options
    const patientsPerPage = parseInt(printSheetsPerPage);
    const isLandscape = printOrientation === 'landscape';
    
    // Grid configuration based on options
    let gridCols, gridRows, pageHeight, fontSize, padding;
    if (patientsPerPage === 6) {
      gridCols = isLandscape ? 3 : 2;
      gridRows = isLandscape ? 2 : 3;
      pageHeight = isLandscape ? '200mm' : '287mm';
      fontSize = '8px';
      padding = '6px';
    } else { // 4 per page
      gridCols = 2;
      gridRows = 2;
      pageHeight = isLandscape ? '200mm' : '287mm';
      fontSize = '10px';
      padding = '10px';
    }
    
    const pages = [];
    for (let i = 0; i < patients.length; i += patientsPerPage) {
      pages.push(patients.slice(i, i + patientsPerPage));
    }
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Cartellini - ${mealLabel} - ${dateLabel}</title>
        <style>
          ${getPrintToolbarCSS()}
          * { box-sizing: border-box; margin: 0; padding: 0; }
          @page { size: A4 ${isLandscape ? 'landscape' : 'portrait'}; margin: 5mm; }
          body { font-family: Arial, sans-serif; font-size: ${fontSize}; }
          .page { 
            width: 100%; 
            height: ${pageHeight}; 
            display: grid; 
            grid-template-columns: repeat(${gridCols}, 1fr); 
            grid-template-rows: repeat(${gridRows}, 1fr); 
            gap: 3mm;
            page-break-after: always;
            padding: 2mm;
          }
          .page:last-child { page-break-after: avoid; }
          .patient-sheet { 
            border: 2px solid #333; 
            padding: ${padding}; 
            overflow: hidden;
            display: flex;
            flex-direction: column;
            border-radius: 6px;
          }
          .patient-header { 
            display: flex; 
            justify-content: space-between; 
            align-items: flex-start; 
            padding-bottom: 6px; 
            margin-bottom: 6px;
            flex-shrink: 0;
            border-bottom: 2px solid #333;
          }
          .room-info { display: flex; align-items: center; gap: 4px; }
          .room-number { 
            font-size: ${patientsPerPage === 6 ? '20px' : '28px'}; 
            font-weight: bold; 
            font-family: monospace; 
            background: #1e293b; 
            color: white; 
            padding: 3px 8px; 
            border-radius: 4px;
          }
          .meal-badge { 
            background: ${mealType === 'colazione' ? '#f59e0b' : mealType === 'pranzo' ? '#f97316' : '#6366f1'}; 
            color: white; 
            padding: 2px 6px; 
            border-radius: 4px; 
            font-size: ${patientsPerPage === 6 ? '8px' : '11px'}; 
            font-weight: bold;
          }
          .location-badge {
            padding: 2px 6px; 
            border-radius: 4px; 
            font-size: ${patientsPerPage === 6 ? '7px' : '10px'}; 
            font-weight: bold;
          }
          .location-reparto { background: #0ea5e9; color: white; }
          .location-ristorante { background: #8b5cf6; color: white; }
          .house-badge { 
            background: #059669; 
            color: white; 
            padding: 2px 6px; 
            border-radius: 4px; 
            font-size: ${patientsPerPage === 6 ? '8px' : '11px'}; 
            font-weight: bold;
          }
          .patient-name { 
            font-size: ${patientsPerPage === 6 ? '12px' : '16px'}; 
            font-weight: bold; 
            margin-top: 4px;
            text-transform: uppercase;
          }
          .header-right { text-align: right; font-size: ${patientsPerPage === 6 ? '7px' : '9px'}; color: #666; }
          .structure-name { font-size: ${patientsPerPage === 6 ? '8px' : '11px'}; font-weight: bold; color: #0284c7; }
          
          .patient-info { 
            background: #f8fafc; 
            border: 1px solid #e2e8f0; 
            border-radius: 4px; 
            padding: 6px; 
            margin-bottom: 6px;
            flex-shrink: 0;
          }
          .info-row { 
            display: flex; 
            align-items: flex-start; 
            gap: 4px; 
            margin-bottom: 3px;
            font-size: ${patientsPerPage === 6 ? '8px' : '10px'};
          }
          .info-row:last-child { margin-bottom: 0; }
          .info-label { 
            font-weight: bold; 
            color: #475569; 
            min-width: ${patientsPerPage === 6 ? '50px' : '65px'};
            flex-shrink: 0;
          }
          .info-value { flex: 1; }
          .diet-badge { 
            display: inline-block; 
            padding: 1px 5px; 
            border-radius: 8px; 
            font-size: ${patientsPerPage === 6 ? '7px' : '9px'}; 
            margin: 1px;
            font-weight: bold;
          }
          .allergy-badge { 
            display: inline-block; 
            background: #fef2f2; 
            color: #dc2626; 
            border: 1px solid #fecaca;
            padding: 1px 5px; 
            border-radius: 8px; 
            font-size: ${patientsPerPage === 6 ? '7px' : '9px'}; 
            margin: 1px;
          }
          .notes-section { 
            background: #fffbeb; 
            border: 1px solid #fcd34d;
            border-radius: 4px;
            padding: 4px 6px;
            font-size: ${patientsPerPage === 6 ? '7px' : '9px'};
            color: #92400e;
            margin-bottom: 6px;
            flex-shrink: 0;
          }
          
          .orders-section { 
            flex: 1; 
            overflow: hidden;
          }
          .orders-title { 
            font-weight: bold; 
            font-size: ${patientsPerPage === 6 ? '9px' : '11px'}; 
            margin-bottom: 4px; 
            color: #1e293b;
            border-bottom: 1px solid #e2e8f0;
            padding-bottom: 3px;
          }
          .order-item { 
            padding: 3px 0; 
            border-bottom: 1px dashed #e2e8f0;
            font-size: ${patientsPerPage === 6 ? '8px' : '10px'};
          }
          .order-item:last-child { border-bottom: none; }
          .order-main { display: flex; justify-content: space-between; align-items: center; }
          .order-category { font-weight: bold; color: #0284c7; }
          .order-dish { margin-left: 6px; }
          .order-side { color: #64748b; font-style: italic; }
          .order-note { font-size: ${patientsPerPage === 6 ? '7px' : '8px'}; color: #f59e0b; margin-top: 2px; }
          .variant-badge { 
            font-size: ${patientsPerPage === 6 ? '6px' : '8px'}; 
            padding: 1px 4px; 
            border-radius: 6px; 
            font-weight: bold;
          }
          .variant-tritato { background: #fee2e2; color: #b91c1c; }
          .variant-pureato, .variant-frullato { background: #fef3c7; color: #b45309; }
          .variant-diabete { background: #dbeafe; color: #1d4ed8; }
          .empty-slot { border: 1px dashed #e2e8f0; background: #f8fafc; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        ${getPrintToolbarHTML('Cartellini Pazienti')}
        <div class="print-content">
        ${pages.map((pagePatients, pageIndex) => `
          <div class="page">
            ${pagePatients.map(patient => {
              const patientConditions = patient.patient_conditions || [];
              const patientAllergies = patient.patient_allergies || [];
              const patientNotes = patient.patient_notes || '';
              const isRistorante = patient.meal_location === 'ristorante';
              
              return `
              <div class="patient-sheet">
                <div class="patient-header">
                  <div>
                    <div class="room-info">
                      <span class="room-number">${patient.patient_room}</span>
                      ${patient.house_prefix ? `<span class="house-badge">${patient.house_prefix}</span>` : ''}
                      <span class="meal-badge">${mealLabel}</span>
                      <span class="location-badge ${isRistorante ? 'location-ristorante' : 'location-reparto'}">${isRistorante ? 'RISTORANTE' : 'REPARTO'}</span>
                    </div>
                    <div class="patient-name">${patient.patient_name}</div>
                  </div>
                  <div class="header-right">
                    <div class="structure-name">${structureName}${patient.house_name ? ` - ${patient.house_name}` : ''}</div>
                    <div>${dateLabel}</div>
                  </div>
                </div>
                
                <div class="patient-info">
                  ${patientConditions.length > 0 ? `
                    <div class="info-row">
                      <span class="info-label">DIETA:</span>
                      <span class="info-value">
                        ${patientConditions.map(code => {
                          const cond = conditions.find(c => c.code === code);
                          return `<span class="diet-badge" style="background:${cond?.color || '#f1f5f9'};color:#333">${cond?.name || code}</span>`;
                        }).join('')}
                      </span>
                    </div>
                  ` : ''}
                  ${patientAllergies.length > 0 ? `
                    <div class="info-row">
                      <span class="info-label">ALLERGIE:</span>
                      <span class="info-value">
                        ${patientAllergies.map(code => {
                          const allergy = allergies.find(a => a.code === code);
                          return `<span class="allergy-badge">⚠ ${allergy?.name || code}</span>`;
                        }).join('')}
                      </span>
                    </div>
                  ` : ''}
                </div>
                
                ${patientNotes ? `
                  <div class="notes-section">
                    <strong>NOTE:</strong> ${patientNotes}
                  </div>
                ` : ''}
                
                <div class="orders-section">
                  <div class="orders-title">PIATTI ORDINATI</div>
                  ${patient.orders.map(order => {
                    const variantClass = order.variant?.includes('tritato') ? 'variant-tritato' : 
                      (order.variant?.includes('pureato') || order.variant?.includes('frullato')) ? 'variant-pureato' : 
                      order.variant?.includes('diabete') ? 'variant-diabete' : '';
                    return `
                      <div class="order-item">
                        <div class="order-main">
                          <span>
                            <span class="order-category">${getCategoryName(order.category)}:</span>
                            <span class="order-dish">${order.dish}</span>
                          </span>
                          ${order.variant !== 'normale' ? `<span class="variant-badge ${variantClass}">${order.variant}</span>` : ''}
                        </div>
                        ${order.note ? `<div class="order-note">→ ${order.note}</div>` : ''}
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>
            `}).join('')}
            ${Array(patientsPerPage - pagePatients.length).fill('<div class="patient-sheet empty-slot"></div>').join('')}
          </div>
        `).join('')}
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    setPrintDialogOpen(false);
  }; 

  const getCategoryName = (code) => {
    const cat = categories.find(c => c.code === code);
    return cat?.name || code;
  };

  const getConditionColor = (code) => {
    const cond = conditions.find(c => c.code === code);
    return cond?.color || '#64748b';
  };

  const getConditionName = (code) => {
    const cond = conditions.find(c => c.code === code);
    return cond?.name || code;
  };

  const getVariantBadge = (variant) => {
    if (variant === 'normale') return null;
    
    const colors = {
      tritato: { bg: '#9333ea20', text: '#9333ea', border: '#9333ea40' },
      pureato: { bg: '#7c3aed20', text: '#7c3aed', border: '#7c3aed40' },
      diabetico: { bg: '#ea580c20', text: '#ea580c', border: '#ea580c40' }
    };
    
    const c = colors[variant] || colors.tritato;
    const label = variant.charAt(0).toUpperCase() + variant.slice(1);
    
    return (
      <span 
        className="text-xs font-medium px-2 py-0.5 rounded-full ml-2"
        style={{ backgroundColor: c.bg, color: c.text, border: `1px solid ${c.border}` }}
      >
        {label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
      </div>
    );
  }

  // Filter patients by current meal type
  const patients = (sheetsData?.patients || []).filter(p => p.meal_type === mealType);

  // Categories for current meal type
  const mealCategories = categories.filter(c => 
    c.meal_type === (mealType === 'colazione' ? 'colazione' : 'pranzo_cena')
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="patient-sheets-page">
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-600" />
              Modifica Ordini - {editingPatient?.patient_name}
            </DialogTitle>
            <DialogDescription>
              Camera {editingPatient?.patient_room} - {mealType.charAt(0).toUpperCase() + mealType.slice(1)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {mealCategories.map(category => {
              const menuData = menuItems[category.code];
              const isSelected = editOrders[category.code]?.selected;
              
              if (!menuData?.dish_name) return null;
              
              return (
                <div 
                  key={category.code}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    isSelected 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => toggleEditOrder(category.code)}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox checked={isSelected} />
                    <div className="flex-1">
                      <div className="font-medium text-slate-700 text-sm uppercase">
                        {category.name}
                      </div>
                      <div className="text-slate-900">
                        {menuData.dish_name}
                      </div>
                      {menuData.side_dish && (
                        <div className="text-sm text-green-600">
                          Contorno: {menuData.side_dish}
                        </div>
                      )}
                      {menuData.allergens?.length > 0 && (
                        <div className="flex items-center gap-1 mt-1">
                          <AlertTriangle className="w-3 h-3 text-amber-500" />
                          <span className="text-xs text-amber-600">
                            Allergeni: {menuData.allergens.join(', ')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleSaveEdit} disabled={savingEdit}>
              {savingEdit ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                'Salva Modifiche'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header - no-print */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 no-print">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-7 h-7 text-blue-700" />
            Cartellini
          </h1>
          <p className="text-slate-500 mt-1">Stampa i cartellini per i vassoi</p>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Date Navigator with Calendar */}
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateDate(-1)}
              data-testid="prev-date-btn"
              className="h-8 w-8 sm:h-9 sm:w-9"
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
            
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="px-2 sm:px-3 py-2 h-auto min-w-[120px] sm:min-w-[160px] hover:bg-slate-50"
                  data-testid="date-picker-btn"
                >
                  <CalendarDays className="w-4 h-4 mr-1 sm:mr-2 text-blue-700" />
                  <span className="font-semibold text-slate-900 text-sm sm:text-base">
                    {format(selectedDate, 'EEE d MMM', { locale: it })}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  locale={it}
                  className="rounded-md border"
                />
              </PopoverContent>
            </Popover>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateDate(1)}
              data-testid="next-date-btn"
              className="h-8 w-8 sm:h-9 sm:w-9"
            >
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
          </div>

          <Button 
            onClick={() => setPrintDialogOpen(true)}
            className="bg-blue-700 hover:bg-blue-800 text-sm"
            size="sm"
            data-testid="print-sheets-btn"
          >
            <Printer className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Stampa Cartellini</span>
            <span className="sm:hidden">Stampa</span>
          </Button>
        </div>
      </div>

      {/* Print Options Dialog */}
      <Dialog open={printDialogOpen} onOpenChange={setPrintDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Opzioni Stampa Cartellini</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Cartellini per foglio A4</Label>
              <Select value={printSheetsPerPage} onValueChange={setPrintSheetsPerPage}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="4">4 cartellini (2x2)</SelectItem>
                  <SelectItem value="6">6 cartellini (2x3 o 3x2)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Orientamento foglio</Label>
              <Select value={printOrientation} onValueChange={setPrintOrientation}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="portrait">Verticale (Portrait)</SelectItem>
                  <SelectItem value="landscape">Orizzontale (Landscape)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Opzione raggruppamento per casa - solo per strutture multi-casa */}
            {isMultiHouse && houses.length > 0 && (
              <div className="space-y-2 pt-2 border-t">
                <Label className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-purple-600" />
                  Raggruppamento
                </Label>
                <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-slate-50"
                  onClick={() => setPrintGroupByHouse(!printGroupByHouse)}
                >
                  <input 
                    type="checkbox" 
                    checked={printGroupByHouse}
                    onChange={(e) => setPrintGroupByHouse(e.target.checked)}
                    className="w-4 h-4 text-purple-600 rounded"
                  />
                  <div>
                    <div className="font-medium text-slate-800 flex items-center gap-2 text-sm">
                      <Home className="w-4 h-4 text-purple-500" />
                      Raggruppa per Casa
                    </div>
                    <p className="text-xs text-slate-500">I cartellini saranno divisi per edificio</p>
                  </div>
                </label>
              </div>
            )}
            
            <div className="pt-2 border-t">
              <p className="text-sm text-slate-500 mb-4">
                {getFilteredPatients().length} cartellini per {mealType.charAt(0).toUpperCase() + mealType.slice(1)}
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPrintDialogOpen(false)}>Annulla</Button>
                <Button onClick={handlePrint} className="bg-blue-700 hover:bg-blue-800">
                  <Printer className="w-4 h-4 mr-2" />
                  Stampa
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Meal Type Toggle + Floor Filter - no-print */}
      <div className="flex flex-wrap items-center gap-3 no-print">
        <div className="flex gap-2">
          {MEALS.map(meal => {
            const Icon = meal.icon;
            const isActive = mealType === meal.code;
            return (
              <Button
                key={meal.code}
                variant={isActive ? 'default' : 'outline'}
                onClick={() => setMealType(meal.code)}
                className={isActive ? 
                  meal.color === 'amber' ? 'bg-amber-500 hover:bg-amber-600' :
                  meal.color === 'orange' ? 'bg-orange-500 hover:bg-orange-600' :
                  'bg-indigo-500 hover:bg-indigo-600'
                : ''}
                data-testid={`meal-${meal.code}`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {meal.name}
              </Button>
            );
          })}
        </div>

        {/* Floor Filter */}
        <Select value={floorFilter} onValueChange={setFloorFilter}>
          <SelectTrigger className="w-40 gap-2" data-testid="floor-filter">
            <Building className="w-4 h-4 text-slate-500 flex-shrink-0" />
            <SelectValue placeholder="Piano" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i piani</SelectItem>
            {availableFloors.map(floor => (
              <SelectItem key={floor} value={floor}>Piano {floor}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* House Filter - Only for Multi-Casa structures */}
        {isMultiHouse && houses.length > 0 && (
          <Select value={houseFilter} onValueChange={setHouseFilter}>
            <SelectTrigger className="w-44 gap-2" data-testid="house-filter">
              <Home className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <SelectValue placeholder="Casa" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le Case</SelectItem>
              {houses.filter(h => h.requires_meal_service !== false).map(house => (
                <SelectItem key={house.id} value={house.id}>
                  {house.prefix} - {house.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Patients Count */}
      <div className="no-print">
        {(() => {
          const mealConfig = MEALS.find(m => m.code === mealType);
          const Icon = mealConfig?.icon || Sun;
          const filteredPatients = getFilteredPatients();
          return (
            <Badge className={`px-4 py-2 text-lg ${
              mealType === 'colazione' ? 'bg-amber-600' :
              mealType === 'pranzo' ? 'bg-orange-600' : 'bg-indigo-600'
            }`}>
              <Icon className="w-4 h-4 mr-2" />
              {filteredPatients.length} pazienti - {mealConfig?.name || 'Pranzo'}
              {floorFilter !== 'all' && ` (Piano ${floorFilter})`}
            </Badge>
          );
        })()}
      </div>

      {(() => {
        const filteredPatients = getFilteredPatients();
        return filteredPatients.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-lg text-slate-500">
              Nessun ordine per {MEALS.find(m => m.code === mealType)?.name?.toLowerCase() || 'il pasto'}
              {floorFilter !== 'all' && ` al piano ${floorFilter}`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div ref={printRef} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredPatients.map((patient) => {
            const mealConfig = MEALS.find(m => m.code === mealType);
            const Icon = mealConfig?.icon || Sun;
            return (
            <Card 
              key={`${patient.patient_id}-${patient.meal_type}`} 
              className={`patient-sheet border-2 ${
                mealType === 'colazione' ? 'border-amber-300' :
                mealType === 'pranzo' ? 'border-orange-300' : 'border-indigo-300'
              }`}
              data-testid={`sheet-${patient.patient_id}`}
            >
              <CardContent className="p-6">
                {/* Patient Header */}
                <div className="flex items-start justify-between mb-4 pb-4 border-b-2 border-slate-200">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="text-3xl font-bold text-slate-900 font-mono bg-slate-100 px-3 py-1 rounded-lg">
                        {patient.patient_room}
                      </div>
                      <Badge className={
                        mealType === 'colazione' ? 'bg-amber-500' :
                        mealType === 'pranzo' ? 'bg-orange-500' : 'bg-indigo-500'
                      }>
                        <Icon className="w-3 h-3 mr-1" />
                        {mealConfig?.shortLabel || 'P'}
                      </Badge>
                      <Badge variant="outline" className={patient.meal_location === 'reparto' ? 'bg-sky-50 text-sky-700 border-sky-300' : 'bg-purple-50 text-purple-700 border-purple-300'}>
                        <MapPin className="w-3 h-3 mr-1" />
                        {patient.meal_location === 'reparto' ? 'Reparto' : 'Ristorante'}
                      </Badge>
                      {patient.house_name && (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300">
                          <Home className="w-3 h-3 mr-1" />
                          {patient.house_prefix || patient.house_name}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xl font-semibold text-slate-800 mt-2 uppercase">
                      {patient.patient_name}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="text-sm text-slate-500">
                      {format(selectedDate, 'd MMM yyyy', { locale: it })}
                    </div>
                    {/* Edit/Delete buttons - no-print, only if user has permission */}
                    {canEditPatientSheets && (
                      <div className="flex gap-1 no-print">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditOrders(patient)}
                          className="h-8 px-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                          title="Modifica ordini"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteOrders(patient)}
                          className="h-8 px-2 text-red-600 border-red-200 hover:bg-red-50"
                          title="Elimina ordini"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Patient Info Section */}
                <div className="bg-slate-50 rounded-lg p-3 mb-4 space-y-2">
                  {/* Dieta */}
                  {patient.patient_conditions?.length > 0 && (
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-bold text-slate-500 min-w-[60px]">🍽️ DIETA:</span>
                      <div className="flex flex-wrap gap-1">
                        {patient.patient_conditions.map(code => (
                          <span
                            key={code}
                            className="text-xs font-medium px-2 py-0.5 rounded-full"
                            style={{
                              backgroundColor: `${getConditionColor(code)}20`,
                              color: getConditionColor(code),
                              border: `1px solid ${getConditionColor(code)}40`
                            }}
                          >
                            {getConditionName(code)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Allergie del paziente */}
                  {patient.patient_allergies?.length > 0 && (
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-bold text-slate-500 min-w-[60px]">⚠️ ALLERGIE:</span>
                      <div className="flex flex-wrap gap-1">
                        {patient.patient_allergies.map(code => {
                          const allergy = allergies.find(a => a.code === code);
                          return (
                            <span
                              key={code}
                              className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200"
                            >
                              {allergy?.name || code}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Note di accettazione */}
                  {patient.patient_notes && (
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-bold text-slate-500 min-w-[60px]">📝 NOTE:</span>
                      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 italic">
                        {patient.patient_notes}
                      </p>
                    </div>
                  )}
                </div>

                {/* Orders Title */}
                <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 pb-2 border-b border-slate-200">
                  Piatti Ordinati
                </div>

                {/* Orders */}
                <div className="space-y-3">
                  {categories.map(category => {
                    const order = patient.orders.find(o => o.category === category.code);
                    if (!order) return null;

                    return (
                      <div 
                        key={category.code}
                        className="flex items-start justify-between py-2 border-b border-slate-100 last:border-0"
                      >
                        <div className="flex-1">
                          <div className="text-sm font-medium text-slate-500 uppercase tracking-wide">
                            {category.name}
                          </div>
                          <div className="text-lg font-medium text-slate-900 mt-0.5">
                            {order.dish}
                            {getVariantBadge(order.variant)}
                          </div>
                          {order.allergens?.length > 0 && (
                            <div className="flex items-center gap-1 mt-1">
                              <AlertTriangle className="w-3 h-3 text-amber-500" />
                              <span className="text-xs font-mono text-amber-600">
                                Allergeni: {order.allergens.join(', ')}
                              </span>
                            </div>
                          )}
                          {order.note && (
                            <div className="text-sm text-amber-700 bg-amber-50 px-2 py-1 rounded mt-1 border border-amber-200">
                              {order.note}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
          })}
        </div>
      );
      })()}
    </div>
  );
};
